import React, { useState, useEffect, useCallback } from 'react';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Modal, Badge, Alert } from 'react-bootstrap';
import {
  FaUserPlus, FaEdit, FaCheck, FaTimes, FaClock,
  FaUsers, FaCalendarCheck, FaCalendarTimes, FaEnvelope, FaSpinner, FaDownload, FaQrcode
} from 'react-icons/fa';
import ExportUtility from '../../components/ExportUtility/ExportUtility';
import WaalikwaModal from '../../modals/waalikwa/WaalikwaModal/WaalikwaModal';
import ScanCardModal from '../../modals/ecards/ScanCardModal';
import { invitationApi } from '../../api/guestList/invitationApi';
import { useAuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import { Dropdown } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Waalikwa.css';
import apiClient from '../../api';
import { useDebounce } from '../../hooks/useDebounce';

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const normaliseRsvp = (rsvp) => {
  if (rsvp === null || rsvp === undefined || rsvp === 0 || rsvp === '0') return 'pending';
  if (rsvp === 1 || rsvp === '1' || rsvp === 'nitaudhuria') return 'nitaudhuria';
  if (rsvp === 2 || rsvp === '2' || rsvp === 'Sitohudhuria' || rsvp === 'sitohudhuria') return 'Sitohudhuria';
  return 'pending';
};

/* ─── component ───────────────────────────────────────────────────────────── */

const Waalikwa = () => {
  const { isAuthenticated, currentEvent } = useAuthContext();

  const [activeCard, setActiveCard] = useState('all');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showScanCardModal, setShowScanCardModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const [mainTab, setMainTab] = useState('invitations');
  const [isExporting, setIsExporting] = useState(false);

  const [visitorsData, setVisitorsData] = useState([]);
  const [stats, setStats] = useState({
    totalInvitations: 0,
    totalGuests: 0,
    nitaudhuria: 0,
    Sitohudhuria: 0,
    pending: 0
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  /* ── fetch with pagination ── */
  const fetchWaalikwa = useCallback(async (p = 1, pp = 10, search = '') => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const params = {
        page: p,
        per_page: pp,
        search: search || undefined
      };
      if (mainTab === 'attendance') {
        response = await invitationApi.getAttendanceList(params);
      } else if (mainTab === 'thank_notes') {
        response = await invitationApi.getThankNoteList(params);
      } else {
        response = await invitationApi.getInvitedGuests(params);
      }

      // Extract API JSON body
      const apiResponse = response?.data ?? response ?? {};
      // Determine if apiResponse has nested data object for pagination metadata
      let paginationData = apiResponse;
      if (apiResponse?.success && apiResponse?.data && !Array.isArray(apiResponse?.data)) {
        paginationData = apiResponse.data;
      }

      // Extract the actual array of guests
      const raw = Array.isArray(paginationData) ? paginationData : (paginationData?.data ?? paginationData?.guests ?? []);

      const creditsVal = apiResponse?.credits ?? paginationData?.credits ?? null;
      const recordsTotal = paginationData?.total_records ?? paginationData?.total ?? raw?.length ?? 0;
      const totalPagesCount = paginationData?.total_pages ?? paginationData?.last_page ?? Math.ceil(recordsTotal / pp);

      // No need to filter by sent_status anymore since /guests/invited handles it
      const filteredRaw = Array.isArray(raw) ? raw : [];

      const formatted = filteredRaw.map((g, index) => ({
        id: g.guest_lists_id || g.guest_list_id || g.id || `${g.card_number || 'guest'}-${index}`,
        guest_lists_id: g.guest_lists_id || g.guest_list_id || g.id,
        guest_list_id: g.guest_lists_id || g.guest_list_id || g.id,
        name: g.name || g.full_name || '',
        phone: g.phone_number || g.mobile || '',
        cardNumber: g.card_number || '—',
        seats: Number(g.card_type) === 2 || g.card_type === '2' || g.card_type === 'Double' ? 2 : 1,
        sendStatus: g.send_status,
        sentVia: g.sent_via,
        generatedStatus: g.generated_status,
        cardType: (g.card_type == 1 || g.card_type === '1') ? 'Single' :
          (g.card_type == 2 || g.card_type === '2') ? 'Double' :
            (g.card_type || 'Single'),
        pledge: g.pledge || 0,
        fund: g.fund || 0,
        status: normaliseRsvp(g.rsvp),
        attended: Number(g.attended) === 1 || Number(g.attendance_status) === 1 || Number(g.is_attended) === 1 || g.attended === true ? 1 : 0,
        dateResponded: g.updated_at ? g.updated_at.split(' ')[0] : null,
      }));

      setVisitorsData(formatted);
      setTotalRecords(recordsTotal);
      setTotalPages(totalPagesCount);
      setCredits(creditsVal);

      // Calculate stats locally from the fetched page's data
      let nitaudhuria = 0;
      let Sitohudhuria = 0;
      let pending = 0;

      formatted.forEach(g => {
        const rsvpStatus = g.status;
        if (rsvpStatus === 'nitaudhuria') {
          nitaudhuria += 1;
        } else if (rsvpStatus === 'Sitohudhuria') {
          Sitohudhuria += 1;
        } else {
          pending += 1;
        }
      });

      setStats({
        totalInvitations: recordsTotal,
        totalGuests: recordsTotal,
        nitaudhuria,
        Sitohudhuria,
        pending
      });
    } catch (err) {
      console.error('Error fetching waalikwa:', err);
      setError('Imeshindwa kupakia data. Jaribu tena.');
    } finally {
      setLoading(false);
    }
  }, [mainTab]);

  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      if (debouncedSearchTerm === '' || debouncedSearchTerm.length >= 3) {
        fetchWaalikwa(page, perPage, debouncedSearchTerm);
      }
    }
  }, [isAuthenticated, currentEvent, page, perPage, mainTab, debouncedSearchTerm]);

  /* ── pagination handlers ── */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleRefresh = () => {
    fetchWaalikwa(page, perPage, searchTerm);
  };

  /* ── PDF & Export ── */
  const exportColumns = [
    { header: 'Na', dataKey: 'index' },
    { header: 'Jina', dataKey: 'name' },
    { header: 'Namba ya Simu', dataKey: 'phone' },
    { header: 'Kadi', dataKey: 'cardNumber' },
    { header: 'Aina ya Kadi', dataKey: 'cardType' },
    { header: 'Mahudhurio', dataKey: 'status' }
  ];

  const fetchExportData = async () => {
    try {
      const response = await apiClient.get('/export/invitations/json');
      const dataPayload = response?.data;

      const eventTitle = dataPayload?.event || currentEvent?.event_title || 'Event';
      const today = new Date();
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      const dd = today.getDate();
      const monthText = monthNames[today.getMonth()];
      const yyyy = today.getFullYear();
      const formattedDate = `${dd} ${monthText} ${yyyy}`;
      const generatedTitle = `${eventTitle} Invitations report printed on ${formattedDate}`;

      const rawData = dataPayload?.invitations || dataPayload?.guests || dataPayload?.data || response?.data || response || [];
      const invitationsList = Array.isArray(rawData) ? rawData : (Array.isArray(rawData?.invitations) ? rawData.invitations : []);

      const data = invitationsList.map((item, index) => {
        const cardTypeLabel = (item.card_type == 1 || item.card_type === '1' || item.card_type === 'Single') ? 'Single' :
          (item.card_type == 2 || item.card_type === '2' || item.card_type === 'Double') ? 'Double' : (item.card_type || 'Single');
        const isAttended = Number(item.attended) === 1 || Number(item.attendance_status) === 1 || Number(item.is_attended) === 1 || item.attended === true;

        return {
          index: index + 1,
          name: item.full_name || item.name || item.guest_name || 'N/A',
          phone: item.mobile || item.phone_number || item.phone || 'N/A',
          cardNumber: item.card_number || '—',
          cardType: cardTypeLabel.toUpperCase(),
          status: isAttended ? 'Amehudhuria' : ''
        };
      });

      return {
        data,
        pdfTitle: generatedTitle
      };
    } catch (err) {
      console.error('Error fetching attendance for export:', err);
      toast.error('Imeshindwa kupakua data ya wageni');
      return [];
    }
  };

  /* ── filter data based on active card, search, and status filter ── */
  const getFilteredData = () => {
    let filtered = visitorsData;

    // Filter by active card (stats card selection)
    if (activeCard === 'nitaudhuria') {
      filtered = filtered.filter(v => v.status === 'nitaudhuria');
    } else if (activeCard === 'Sitohudhuria') {
      filtered = filtered.filter(v => v.status === 'Sitohudhuria');
    } else if (activeCard === 'pending') {
      filtered = filtered.filter(v => v.status === 'pending');
    } else if (activeCard === 'responded') {
      filtered = filtered.filter(v => v.status !== 'pending');
    }

    // Filter by status dropdown
    if (filterStatus !== 'all') {
      filtered = filtered.filter(v => v.status === filterStatus);
    }

    // Local Search Filter has been removed as it is handled by the server
    return filtered;
  };

  const filteredData = getFilteredData();

  const percentages = {
    nitaudhuria: stats.totalGuests > 0 ? Math.round((stats.nitaudhuria / stats.totalGuests) * 100) : 0,
    Sitohudhuria: stats.totalGuests > 0 ? Math.round((stats.Sitohudhuria / stats.totalGuests) * 100) : 0,
    pending: stats.totalGuests > 0 ? Math.round((stats.pending / stats.totalGuests) * 100) : 0,
    responseRate: stats.totalGuests > 0
      ? Math.round(((stats.nitaudhuria + stats.Sitohudhuria) / stats.totalGuests) * 100)
      : 0,
  };

  /* ── RSVP update ── */
  const handleOpenUpdateModal = (guest) => {
    setSelectedGuest(guest);
    setNewStatus(guest.status);
    setShowUpdateModal(true);
  };

  const handleUpdateStatus = async () => {
    try {
      let rsvpValue = 0;
      if (newStatus === 'nitaudhuria') rsvpValue = 1;
      else if (newStatus === 'Sitohudhuria') rsvpValue = 2;

      const guestListId = selectedGuest?.guest_lists_id || selectedGuest?.guest_list_id || selectedGuest?.id;
      const guestListIdNum = Number(guestListId) || guestListId;

      const payload = {
        guest_lists_id: guestListIdNum,
        guest_list_id: guestListIdNum,
        guest_count: Number(selectedGuest?.seats) || 1,
        remarks: 'Sawa',
        status: rsvpValue
      };

      const response = await invitationApi.markAttendance(payload);

      handleApiResponse(response);
      setShowUpdateModal(false);
      fetchWaalikwa(page, perPage, searchTerm);
    } catch (err) {
      console.error(err);
      handleApiResponse(err);
    }
  };

  const handleBackendExport = async (type = 'pdf') => {
    setIsExporting(true);
    try {
      const eventName = currentEvent?.event_title || 'Waalikwa';
      await invitationApi.exportInvitations(type, `${eventName}_Invitations`);
      toast.success('Download started!');
    } catch (err) {
      console.error(err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  /* ── status badge cell ── */
  const statusCell = (row) => {
    const styles = {
      nitaudhuria: { color: '#2e7d32', border: '1px solid #2e7d32' },
      Sitohudhuria: { color: '#c62828', border: '1px solid #c62828' },
      pending: { color: '#f57c00', border: '1px solid #f57c00' },
    };
    const labels = {
      nitaudhuria: 'Nitaudhuria',
      Sitohudhuria: 'Sitohudhuria',
      pending: 'Pending',
    };
    const base = {
      background: 'transparent',
      borderRadius: '10px',
      padding: '0.1rem 0.8rem',
      fontSize: '0.85rem',
      fontWeight: '500',
      display: 'inline-block',
    };
    return (
      <span style={{ ...base, ...(styles[row.status] || styles.pending) }}>
        {labels[row.status] || 'Pending'}
      </span>
    );
  };

  /* ── sent-via badge ── */
  const sentViaCell = (row) => {
    if (row.sendStatus === 0) return <Badge bg="secondary">Haijatumwa</Badge>;
    if (row.sentVia === 1) return <Badge bg="primary">SMS</Badge>;
    if (row.sentVia === 2) return <Badge bg="success">WhatsApp</Badge>;
    return <Badge bg="info">Imetumwa</Badge>;
  };

  /* ── attendance badge cell ── */
  const attendanceCell = (row) => {
    if (row.attended === 1) {
      return (
        <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-2.5 py-1 fw-semibold" style={{ fontSize: '0.8rem' }}>
          Amehudhuria
        </span>
      );
    }
    return (
      <span className="badge rounded-pill bg-warning-subtle text-dark border border-warning-subtle px-2.5 py-1 fw-semibold" style={{ fontSize: '0.8rem' }}>
        Pending
      </span>
    );
  };

  /* ── columns ── */
  const columns = [
    { name: '#', cell: (row, index) => (page - 1) * perPage + index + 1, width: '55px', center: true },
    { name: 'Jina', selector: r => r.name, sortable: true, grow: 2 },
    {
      name: 'No Ya Simu',
      selector: r => r.phone,
      sortable: true,
      cell: r => (
        <a href={`tel:${r.phone}`} style={{ textDecoration: 'none', color: '#4169e1' }}>
          {r.phone}
        </a>
      ),
    },
    {
      name: 'Kadi namba',
      selector: r => r.cardNumber,
      sortable: true,
      cell: r => <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{r.cardNumber}</span>,
      width: '115px',
    },
    {
      name: 'Card Type',
      selector: r => r.cardType,
      sortable: true,
      width: '105px',
    },
    {
      name: 'RSVP',
      selector: r => r.status,
      sortable: true,
      cell: statusCell,
      width: '135px',
    },
    {
      name: 'Mahudhurio',
      selector: r => r.attended,
      sortable: true,
      cell: attendanceCell,
      width: '135px',
      center: true,
    },
    {
      name: 'Actions',
      cell: row => (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleOpenUpdateModal(row)}
          style={{ borderRadius: '8px', padding: '0.25rem 0.65rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
        >
          Badilisha
        </Button>
      ),
      width: '130px',
      center: true,
    },
  ];

  /* ── loading / error ── */
  if (loading && visitorsData.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Inapakia wageni waalikwa...</p>
        </div>
      </div>
    );
  }

  /* ── render ── */
  return (
    <div className="container">
      <div className="row">
        <div className="container-fluid mt-4">
          <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Waalikwa</h1>

          {/* Navigation Tabs */}


          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}{' '}
              <Button variant="link" size="sm" onClick={() => fetchWaalikwa(page, perPage)}>Jaribu tena</Button>
            </Alert>
          )}

          {/* Credits info */}
          {credits !== null && (
            <div className="text-center mb-2">
              <small className="text-muted">
                <FaEnvelope className="me-1" />
                SMS Credits zilizobaki: <strong>{credits}</strong>
              </small>
            </div>
          )}

          {/* Statistics Section */}
          <div className="dashboard row mt-4">
            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'all' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('all')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaUsers className="mb-2" size={24} style={{ color: '#007bff' }} />
                  <div>Mialiko Yote</div>
                  <div className="dashboard-stats-number">
                    {stats.totalInvitations}
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'nitaudhuria' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('nitaudhuria')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaCalendarCheck className="mb-2" size={24} style={{ color: '#28a745' }} />
                  <div>Nitaudhuria</div>
                  <div className="dashboard-stats-number">
                    {stats.nitaudhuria}{' '}
                    <small className="stats-percentage">({percentages.nitaudhuria}%)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'Sitohudhuria' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('Sitohudhuria')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaCalendarTimes className="mb-2" size={24} style={{ color: '#dc3545' }} />
                  <div>Sitohudhuria</div>
                  <div className="dashboard-stats-number">
                    {stats.Sitohudhuria}{' '}
                    <small className="stats-percentage">({percentages.Sitohudhuria}%)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className={`dashboard bg-dash p-4 rounded-lg ${activeCard === 'pending' ? 'card-active' : ''}`}
                onClick={() => setActiveCard('pending')}
                style={{ cursor: 'pointer' }}
              >
                <div className="dashText">
                  <FaClock className="mb-2" size={24} style={{ color: '#ffc107' }} />
                  <div>Hawajajibu</div>
                  <div className="dashboard-stats-number">
                    {stats.pending}{' '}
                    <small className="stats-percentage">({percentages.pending}%)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard col text-center">
              <div
                className="dashboard bg-dash action-card-btn p-3 rounded-lg h-100 d-flex flex-column align-items-center justify-content-center"
                onClick={() => setShowScanCardModal(true)}
                style={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                  border: '1px solid #818cf8'
                }}
              >
                <div className="dashText text-center" style={{ color: '#3730a3' }}>
                  <FaQrcode className="mb-2" size={24} />
                  <div className="fw-semibold" style={{ fontSize: '0.9rem', lineHeight: '1.25' }}>Hakiki Kadi</div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="d-flex justify-content-between align-items-center my-4">
            <div className="d-flex gap-3 flex-grow-1 me-3">
              <Form.Group controlId="searchInput" className="flex-grow-1">
                <Form.Control
                  type="text"
                  placeholder="Tafuta kwa jina, simu au namba ya kadi..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  style={{ borderRadius: '8px', height: '38px' }}
                />
              </Form.Group>

            </div>

            <div className="d-flex gap-2 align-items-center">

              <Button
                variant="outline-secondary"
                onClick={handleRefresh}
                style={{ borderRadius: '8px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500' }}
              >
                Refresh
              </Button>
              <ExportUtility
                fetchData={fetchExportData}
                columns={exportColumns}
                filename={`${currentEvent?.event_title || 'Waalikwa'}_Invitations`}
                pdfTitle={`${currentEvent?.event_title || 'Event'} Confirmation Invitations report printed on ${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}`}
                orientation="landscape"
                pdfOnlyButton={true}
                pdfButtonText="Waalikwa PDF"
                btnClassName="btn btn-outline-danger d-flex align-items-center gap-2"
                btnStyle={{ borderRadius: '8px', height: '38px', fontWeight: '500', display: 'flex', alignItems: 'center' }}
              />
            </div>
          </div>


          {/* Data Table without Pagination */}
          <StyledDataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            emptyMessage="Hakuna wageni waalikwa walioorodheshwa bado."
            pagination={true}
            paginationServer={true}
            paginationTotalRows={totalRecords}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            paginationDefaultPage={page}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerPageChange}
          />
        </div>
      </div>

      {/* Add Guest Modal */}
      <WaalikwaModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        guest={selectedGuest}
      />

      {/* Update RSVP Status Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Badilisha Hali ya RSVP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGuest && (
            <>
              <div className="guest-info mb-4 p-3 bg-light rounded">
                <h6>Taarifa za Mgeni</h6>
                <p className="mb-1"><strong>Jina:</strong> {selectedGuest.name}</p>
                <p className="mb-1"><strong>Simu:</strong> {selectedGuest.phone}</p>
                <p className="mb-0"><strong>Kadi:</strong> {selectedGuest.cardNumber} ({selectedGuest.seats} viti)</p>
              </div>
              <Form.Group>
                <Form.Label className="fw-bold">Chagua Hali Mpya</Form.Label>
                <div className="status-options d-flex gap-2">
                  <Button
                    disabled
                    variant={newStatus === 'nitaudhuria' ? 'success' : 'outline-success'}
                    className="flex-fill"
                    onClick={() => setNewStatus('nitaudhuria')}
                  >
                    <FaCheck className="me-2" />
                    Nitaudhuria
                  </Button>
                  <Button
                    disabled
                    variant={newStatus === 'Sitohudhuria' ? 'danger' : 'outline-danger'}
                    className="flex-fill"
                    onClick={() => setNewStatus('Sitohudhuria')}
                  >
                    <FaTimes className="me-2" />
                    Sitohudhuria
                  </Button>
                  <Button
                    disabled
                    variant={newStatus === 'pending' ? 'warning' : 'outline-warning'}
                    className="flex-fill"
                    onClick={() => setNewStatus('pending')}
                  >
                    <FaClock className="me-2" />
                    Hajajibu
                  </Button>
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>Funga</Button>
          <Button disabled variant="primary" onClick={handleUpdateStatus}>Coming Soon</Button>
        </Modal.Footer>
      </Modal>

      <ScanCardModal
        show={showScanCardModal}
        onHide={() => setShowScanCardModal(false)}
        onAttendanceMarked={handleRefresh}
      />
    </div>
  );
};

export default Waalikwa;